import {
  Box,
  Button,
  Divider,
  Flex,
  Image,
  Link,
  Space,
  Stack,
  Text,
  TonicProps,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProvisionApi, RiskAIApi } from 'src/apis';
import { useDashboardDaysStore } from 'src/models/useDashboardDaysModel';
import { useDashboardServicesStore } from 'src/models/useDashboardServicesModel';

import iconBec from 'src/assets/image/icon-bec.svg';
import iconMaliciousFile from 'src/assets/image/icon-malicious-file.svg';
import iconMaliciousUrl from 'src/assets/image/icon-malicious-url.svg';
import iconPhishing from 'src/assets/image/icon-phishing.svg';
import iconRansomware from 'src/assets/image/icon-ransomware.svg';
import iconAnomaly from 'src/assets/image/icon-anomaly.svg';
import iconScam from 'src/assets/image/icon-scam.svg';
import TextEllipsis from '@Components/Common/Tooltip/TextEllipsis';
import { ThreatType } from '@Utils/enums';
import { getServiceNamesByValues } from '@Utils/service';
import { generateRedirectUrlToLog } from '@Log/shared';
import DashboardLayout from '@Dashboard/Layout/DashboardLayout';
import TimeRangeSelector from '@Dashboard/TimeRangeSelector';
import {
  computeBec,
  computeMaliciousFile,
  computeMaliciousURL,
  computePhishing,
  computeRansomware,
  computeRiskAiAnomaly,
  computeScam,
  computeTotal,
} from './computer';
import RedirectLink from '@Components/RedirectLink';
import { Info } from '@Components/Common/Info';
import { handleNavigate, isInUIC } from '@Utils/common';
import { useNavigate } from 'react-router-dom';
import { useOrgStore } from '../../../../models/useOrgModel';
import { useQuery } from '@tanstack/react-query';
import { DashboardPeriod } from '@Types/dashboard';
import { PolicyType } from '@Types/app';
import { useDashboardWidget } from '../../useDashboardWidget';

enum LogTypeEnum {
  DETECTION = 'detection',
  ADUIT = 'audit',
  INLINETRACKING = 'inlineTracking',
  QUARANTINE = 'quarantine',
  APIINTEGRATION = 'apiIntegration',
  URLTRACKING = 'urlTracking',
}

const OverallThreatDetection = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgId } = useOrgStore();

  const daysModel = useDashboardDaysStore();
  const servicesModel = useDashboardServicesStore();
  const day = daysModel.days['OverallThreatDetection'];

  const { data } = useDashboardWidget(
    'overallThreatDetections',
    day as DashboardPeriod
  );

  const { data: anomalyConfigured, isLoading: isAnomalyQueryLoading } =
    useQuery({
      queryKey: ['anomalyConfigured', orgId],
      queryFn: () => RiskAIApi.getAnomalyConfigured(orgId),
      select: ({ data }) => data,
    });

  const { data: provisionedServices, isLoading: isProvisionQueryLoading } =
    useQuery({
      queryKey: ['provisionedServices', orgId],
      queryFn: () => ProvisionApi.getProvisionedServicesByOrgId(orgId),
      select: ({ data }) => data,
    });

  const isExchangeOnlineProvisioned = useMemo(() => {
    return (provisionedServices ?? []).some((item) =>
      [PolicyType.Exchange].includes(item)
    );
  }, [provisionedServices]);

  const showRiskAIButton =
    isExchangeOnlineProvisioned &&
    !anomalyConfigured &&
    !isAnomalyQueryLoading &&
    !isProvisionQueryLoading;

  const Item = (
    props: {
      icon: ReactNode;
      label: ReactNode;
      count?: number;
      info?: ReactNode;
      large?: boolean;
      type?: ThreatType;
      children?: ReactNode;
    } & TonicProps
  ) => {
    const { icon, label, count, info, large, type, children, ...boxProps } =
      props;
    return (
      <Box width="0" flex="1" padding="16px" {...boxProps}>
        <Stack
          direction="column"
          spacing={large ? '15px' : '20px'}
          width="calc(12vw-20px)"
          height="100%"
        >
          <Flex alignItems="center" columnGap="8px">
            {icon}
            <Box
              display="inline"
              verticalAlign="middle"
              fontWeight="semibold"
              fontSize="18px"
              color={
                colorMode === 'dark'
                  ? colorStyle.color.primary
                  : colorStyle.color.emphasis
              }
              overflow="hidden"
            >
              <TextEllipsis placement="top">{label}</TextEllipsis>
            </Box>
            {info && (
              <Info
                placement="top-end"
                contentStyle={{ maxWidth: '304px' }}
                content={info}
              />
            )}
          </Flex>
          {count === undefined ? null : type !== undefined && count !== 0 ? (
            <RedirectLink
              href={`logs?${generateRedirectUrlToLog(
                [
                  {
                    field: 'threatType',
                    values: [type.toString()],
                    type: 'terms',
                  },
                  {
                    field: 'scanSource',
                    values: getServiceNamesByValues(
                      servicesModel.enabledServices
                    ),
                    type: 'terms',
                  },
                ],
                day,
                LogTypeEnum.DETECTION
              )}`}
            >
              <Text
                fontSize={large ? '48px' : '32px'}
                lineHeight={large ? '48px' : '40px'}
                overflow="hidden"
              >
                <TextEllipsis>{count}</TextEllipsis>
              </Text>
            </RedirectLink>
          ) : (
            <Text
              fontSize={large ? '48px' : '32px'}
              lineHeight={large ? '48px' : '40px'}
              color={colorStyle.color.primary}
              overflow="hidden"
            >
              <TextEllipsis>{count}</TextEllipsis>
            </Text>
          )}
          {children}
        </Stack>
      </Box>
    );
  };

  return (
    <DashboardLayout
      title={t('dashboard.sideBar.threatOverall')}
      barColor="#868686"
      titleBar={<TimeRangeSelector widgetId="OverallThreatDetection" />}
    >
      <Flex alignItems="flex-end" gridColumn="1 / 3">
        <Text fontSize="56px" lineHeight="56px">
          {computeTotal(data)}
        </Text>
        <Space width="16px" />
        <Text fontSize="16px">
          {t('dashboard.chart.overallThreatDetection.emailMessageAndFile')}
        </Text>
      </Flex>
      <Divider
        width="100%"
        display="block"
        gridColumn="1 / 3"
        borderTop="2px solid"
      />
      <Flex gridRow="auto/span 1" gridColumn="auto/span 2" height="max-content">
        <Item
          icon={<Image src={iconBec} width={20} height={20} />}
          label={t('dashboard.chart.overallThreatDetection.bec')}
          count={computeBec(data)}
          type={ThreatType.BEC}
        />
        <Divider orientation="vertical" borderLeft="2px solid" my="20px" />

        <Item
          icon={<Image src={iconPhishing} width={20} height={20} />}
          label={t('dashboard.chart.overallThreatDetection.phishing')}
          count={computePhishing(data)}
          type={ThreatType.Phishing}
        />
        <Divider orientation="vertical" borderLeft="2px solid" my="20px" />
        <Item
          icon={<Image src={iconRansomware} width={20} height={20} />}
          label={t('dashboard.chart.overallThreatDetection.ransomware')}
          count={computeRansomware(data)}
          type={ThreatType.Ransomware}
        />
        <Divider orientation="vertical" borderLeft="2px solid" my="20px" />
        <Item
          icon={<Image src={iconMaliciousFile} width={20} height={20} />}
          label={t('dashboard.chart.overallThreatDetection.maliciousFile')}
          count={computeMaliciousFile(data)}
          type={ThreatType.MaliciousFile}
        />
        <Divider orientation="vertical" borderLeft="2px solid" my="20px" />
        <Item
          icon={<Image src={iconMaliciousUrl} width={20} height={20} />}
          label={t('dashboard.chart.overallThreatDetection.maliciousUrl')}
          count={computeMaliciousURL(data)}
          type={ThreatType.MaliciousURL}
        />
        <Divider orientation="vertical" borderLeft="2px solid" my="20px" />
        <Item
          icon={<Image src={iconScam} width={20} height={20} />}
          label={t('dashboard.chart.overallThreatDetection.scam')}
          count={computeScam(data)}
          type={ThreatType.Scam}
        />
        <Divider orientation="vertical" borderLeft="2px solid" my="20px" />
        <Item
          icon={<Image src={iconAnomaly} width={20} height={20} />}
          label={t('dashboard.chart.overallThreatDetection.anomalies')}
          count={!showRiskAIButton ? computeRiskAiAnomaly(data) : undefined}
          info={
            <Box>
              <Text>
                {t('dashboard.chart.overallThreatDetection.anomalies.info')}
              </Text>
              <Link
                href={
                  isInUIC()
                    ? t(
                        'dashboard.chart.overallThreatDetection.anomalies.action.hrefV1'
                      )
                    : t(
                        'dashboard.chart.overallThreatDetection.anomalies.action.hrefCAS'
                      )
                }
                target="_blank"
              >
                {t('common.action.label.learnMore')}
              </Link>
            </Box>
          }
          type={ThreatType.Anomaly}
          width="max-content"
        >
          {showRiskAIButton && (
            <Button
              variant="primary"
              mt="auto"
              onClick={() => {
                handleNavigate('/policy/advanced-threat-protection/email');
                navigate('/policy/advanced-threat-protection/email');
              }}
            >
              {t('dashboard.chart.overallThreatDetection.anomalies.action')}
            </Button>
          )}
        </Item>
      </Flex>
    </DashboardLayout>
  );
};
export default OverallThreatDetection;
